import React, { useState, useEffect, useMemo } from "react";
import { Service, Professional } from "../../../types";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  Loading,
  ErrorState,
  Select,
  SearchBar,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { CreateServiceModal } from "./CreateServiceModal";
import { AdminSubServices } from "./AdminSubServices";
import { ServicesListView } from "./ServicesListView";
import { ServiceSubServicesModal } from "./ServiceSubServicesModal";
import { getServiceIcon } from "../../../utils/themeUtils";
import { Icons } from "../../shared/Icons";
import { servicesApi, professionalsApi } from "../../../services";

export const AdminServices: React.FC = () => {
  const { company } = useCompany();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : null;
  const isBarbershop = company?.category === "barbershop";
  const showProfessionalFilter = isBarbershop && !isProfessional;

  const [services, setServices] = useState<Service[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<
    number | null
  >(null);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"services" | "subservices">(
    "services"
  );
  const [subServicesShowCreateForm, setSubServicesShowCreateForm] =
    useState(false);
  const [showSubServicesModal, setShowSubServicesModal] = useState(false);
  const [serviceForSubServices, setServiceForSubServices] =
    useState<Service | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Handle header button click based on active tab
  const handleHeaderButtonClick = () => {
    if (activeTab === "services") {
      setShowCreateForm(true);
    } else {
      setSubServicesShowCreateForm(true);
    }
  };

  // Handle tab change
  const handleTabChange = (tab: "services" | "subservices") => {
    setActiveTab(tab);
    // Refresh services when switching to services tab
    if (tab === "services") {
      fetchServices();
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfessional, loggedInProfessionalId]);

  useEffect(() => {
    const fetchProfessionals = async () => {
      if (!showProfessionalFilter || !company?.id) return;

      setLoadingProfessionals(true);
      try {
        const response = await professionalsApi.getProfessionalsByCompany();
        if (response.success && response.data) {
          setProfessionals(
            response.data.filter((professional) => professional.isActive !== false)
          );
        }
      } catch (err) {
        console.error("Failed to load professionals:", err);
      } finally {
        setLoadingProfessionals(false);
      }
    };

    fetchProfessionals();
  }, [showProfessionalFilter, company?.id]);

  const displayedServices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return services.filter((service) => {
      if (
        selectedProfessionalId != null &&
        service.professionalId !== selectedProfessionalId
      ) {
        return false;
      }
      if (!query) return true;
      return (
        service.name.toLowerCase().includes(query) ||
        (service.description
          ? service.description.toLowerCase().includes(query)
          : false) ||
        (service.professional?.name
          ? service.professional.name.toLowerCase().includes(query)
          : false)
      );
    });
  }, [services, selectedProfessionalId, searchQuery]);

  const fetchServices = async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      // If user is a professional, fetch only their services
      if (isProfessional && loggedInProfessionalId) {
        response = await servicesApi.getServicesByProfessional(loggedInProfessionalId);
      } else {
        // If user is company admin, fetch all services
        response = await servicesApi.getServicesByCompany();
      }

      if (response.success && response.data) {
        setServices(response.data);
      } else {
        setError(response.error || "Failed to load services");
      }
    } catch (err) {
      setError("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setShowCreateForm(true);
  };

  const handleManageSubServices = (service: Service) => {
    setServiceForSubServices(service);
    setShowSubServicesModal(true);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingService(null);
  };

  const handleServiceSaved = () => {
    fetchServices();
    setShowCreateForm(false);
    setEditingService(null);
  };

  const handleDelete = (id: number) => {
    setServiceToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!serviceToDelete) return;

    setDeleteLoading(true);
    setError(null);

    try {
      const response = await servicesApi.deleteService(serviceToDelete);
      if (response.success) {
        await fetchServices();
        setShowDeleteModal(false);
        setServiceToDelete(null);
      } else {
        setError(response.error || "Failed to delete service");
      }
    } catch (err) {
      setError("Failed to delete service");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setServiceToDelete(null);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={getServiceIcon(company)}
        title="Gerenciamento de Serviços"
        subtitle="Gerencie todos os serviços e sub-serviços do seu negócio"
        buttonIcon={<Icons.Add sx={{ fontSize: 20 }} />}
        buttonText={
          activeTab === "services" ? "Novo Serviço" : "Novo Sub-serviço"
        }
        onButtonClick={handleHeaderButtonClick}
        buttonDisabled={loading}
        showViewToggle={false}
        midleContent={
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={
              activeTab === "services"
                ? "Buscar serviços..."
                : "Buscar sub-serviços..."
            }
            className="w-full sm:w-64"
          />
        }
        rightContent={
          showProfessionalFilter ? (
            <Select
              value={selectedProfessionalId ?? "all"}
              onChange={(value) => {
                if (value === "all" || value === "") {
                  setSelectedProfessionalId(null);
                  return;
                }
                setSelectedProfessionalId(
                  typeof value === "number" ? value : parseInt(String(value), 10)
                );
              }}
              options={[
                { value: "all", label: "Todos os profissionais" },
                ...professionals.map((professional) => ({
                  value: professional.id,
                  label: professional.name,
                  image: professional.picture ?? null,
                })),
              ]}
              placeholder="Filtrar por profissional"
              disabled={loadingProfessionals}
              className="min-w-[200px] w-full sm:w-auto"
            />
          ) : undefined
        }
      />

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="border-b border-gray-200 dark:border-slate-700">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => handleTabChange("services")}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "services"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-500"
              }`}
            >
              {getServiceIcon(company)} Serviços
            </button>
            <button
              onClick={() => handleTabChange("subservices")}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "subservices"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-500"
              }`}
            >
              {getServiceIcon(company)} Sub-serviços
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === "services" ? (
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <ErrorState
                message={error}
                variant="card"
                onRetry={() => fetchServices()}
              />
            )}

            <CreateServiceModal
              isOpen={showCreateForm}
              onClose={handleCancel}
              onSuccess={handleServiceSaved}
              editingService={editingService}
              companyCategory={company?.category}
            />

            {/* Services Content */}
            {loading && !showCreateForm ? (
              <Loading
                message="Carregando serviços..."
                size="xl"
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-12"
              />
            ) : (
              <Card className="p-8">
                {services.length === 0 ? (
                  <EmptyState
                    icon={getServiceIcon(company)}
                    title="Nenhum serviço encontrado"
                    description="Crie seu primeiro serviço para começar!"
                    actionText="Criar Primeiro Serviço"
                    onAction={() => setShowCreateForm(true)}
                  />
                ) : displayedServices.length === 0 ? (
                  <EmptyState
                    icon={getServiceIcon(company)}
                    title={
                      searchQuery.trim()
                        ? "Nenhum serviço encontrado"
                        : "Nenhum serviço deste profissional"
                    }
                    description={
                      searchQuery.trim()
                        ? "Tente buscar com outros termos"
                        : "Não há serviços cadastrados para o profissional selecionado."
                    }
                  />
                ) : (
                  <ServicesListView
                    services={displayedServices}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onManageSubServices={handleManageSubServices}
                    loading={loading}
                    companyCategory={company?.category}
                  />
                )}
              </Card>
            )}

            <ConfirmationModal
              isOpen={showDeleteModal}
              title="Excluir Serviço"
              message="Tem certeza que deseja excluir este serviço? Esta ação não pode ser desfeita."
              confirmText="Excluir"
              cancelText="Cancelar"
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
              loading={deleteLoading}
            />

            {/* SubServices Management Modal */}
            {serviceForSubServices && (
              <ServiceSubServicesModal
                isOpen={showSubServicesModal}
                onClose={() => {
                  setShowSubServicesModal(false);
                  setServiceForSubServices(null);
                }}
                serviceId={serviceForSubServices.id}
                onRefresh={fetchServices}
              />
            )}
          </div>
        ) : (
          <AdminSubServices
            showCreateForm={subServicesShowCreateForm}
            onCloseCreateForm={() => setSubServicesShowCreateForm(false)}
            viewMode="list"
            searchQuery={searchQuery}
          />
        )}
      </div>
    </div>
  );
};

export default AdminServices;
